'use client';

import { ImageLoading } from '@pixum/logo';
import { ImageFetchPriority } from '../../../image/constants';
import { Image } from '../../../image';
import type { Device, ImageNode, PictureDataProps } from 'data-layer/types';
import { redirectToWithPageReload } from 'utils';
import { getMediaAspectRatioStyle } from '../../utils/getMediaAspectRatioStyle';
import '../../mediaWrapper.css';

export interface MagazinePictureProps {
  image: ImageNode;
  pictureData?: PictureDataProps[];
  device: Device;
  isUsedAsKeyVisual?: boolean;
  clickUrl?: string;
}

/**
 * MagazinePicture component - Optimized responsive picture element for Magazine.
 *
 * Renders a responsive `<picture>` element with device-specific image variants
 * for optimal LCP (Largest Contentful Paint) performance when used as KeyVisual.
 *
 * @param props - Picture configuration including image data and variants
 * @returns Rendered picture element
 *
 * @example
 * // Magazine KeyVisual with responsive images
 * <MagazinePicture
 *   image={{
 *     url: 'magazine-hero.jpg',
 *     alt: 'Magazine hero image',
 *     title: 'Main magazine',
 *   }}
 *   pictureData={[
 *     { media: '(min-width: 768px)', srcset: 'hero-lg.jpg 2x, hero-md.jpg 1x' },
 *     { media: '(max-width: 767px)', srcset: 'hero-sm.jpg 1x' },
 *   ]}
 *   device="desktop"
 *   isUsedAsKeyVisual={true}
 * />
 */
export function MagazinePicture(props: MagazinePictureProps) {
  const { image, pictureData, device, isUsedAsKeyVisual = false, clickUrl } = props;

  const safeAlt = image?.alt || '';
  const safeTitle = image?.title || '';
  const safeSrc = image?.url || '';

  const loading = isUsedAsKeyVisual ? ImageLoading.Eager : ImageLoading.Lazy;
  const fetchPriority = isUsedAsKeyVisual ? ImageFetchPriority.High : ImageFetchPriority.Low;

  const handleClick = clickUrl ? () => redirectToWithPageReload(clickUrl) : undefined;

  const renderAsPicture = pictureData && pictureData.length > 0;

  // The wrapper reserves the image box up front so the layout does not shift when the image arrives (also without JS)
  return (
    <div className="magazine-media-wrapper" style={getMediaAspectRatioStyle(image)}>
      {renderAsPicture ? (
        <picture onClick={handleClick}>
          {pictureData.map((source, index) => (
            <source key={index} media={source.media} srcSet={source.srcset} />
          ))}
          <img
            src={safeSrc}
            alt={safeAlt}
            title={safeTitle}
            width={image?.imageTagWidth}
            height={image?.imageTagHeight}
            loading={loading}
            fetchPriority={fetchPriority}
          />
        </picture>
      ) : (
        <Image image={image} device={device} loading={loading} fetchPriority={fetchPriority} onClick={handleClick} />
      )}
    </div>
  );
}
